import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { type PnlV2Header } from './pnl-types'

interface PnlSummaryCardsProps {
  header: PnlV2Header | null
}

interface CardDef {
  label: string
  value: number
}

export function formatUsdSigned (v: number): string {
  const sign = v < 0 ? '-' : '+'
  const abs = Math.abs(v)
  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// Color is determined solely by the final sign (design §7.2): green for
// income, red for cost, neutral for ~zero. Funding/fees are NOT pinned to a
// fixed color and no abs() is taken anywhere.
function signColor (v: number): string {
  if (v > 0.005) return '#4caf50'
  if (v < -0.005) return '#f44336'
  return '#b0b0b0'
}

const PnlSummaryCards: React.FC<PnlSummaryCardsProps> = ({ header }) => {
  if (header === null) return null

  const cards: CardDef[] = [
    { label: 'Period PnL', value: header.period_pnl },
    { label: 'Realized', value: header.realized },
    { label: 'Unrealized Δ', value: header.unrealized_change },
    { label: 'Funding', value: header.funding },
    { label: 'Fees', value: header.fee },
    { label: 'Rebate / Adj', value: header.rebate + header.adjustment },
  ]

  const formula = `${formatUsdSigned(header.period_pnl)} = ` +
    `${formatUsdSigned(header.realized)} (realized) ` +
    `${formatUsdSigned(header.unrealized_change)} (unrealized Δ) ` +
    `${formatUsdSigned(header.funding)} (funding) ` +
    `${formatUsdSigned(header.fee)} (fees) ` +
    `${formatUsdSigned(header.rebate + header.adjustment)} (rebate/adj)`

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {cards.map((c) => (
          <Card key={c.label} sx={{ flex: '1 1 150px', minWidth: 140 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant='caption' color='text.secondary'>{c.label}</Typography>
              <Typography
                variant='h6'
                sx={{ color: signColor(c.value), fontFamily: 'monospace', mt: 0.5 }}
              >
                {formatUsdSigned(c.value)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      <Typography
        variant='caption'
        sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontFamily: 'monospace' }}
      >
        {formula}
      </Typography>
    </Box>
  )
}

export default PnlSummaryCards
